<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  conversations: {
    type: Array,
    default: () => [],
  },
  loadingConversations: {
    type: Boolean,
    default: false,
  },
  activeConversationId: {
    type: String,
    default: null,
  },
  sidebarExpanded: {
    type: Boolean,
    required: true,
  },
  sectionOpen: {
    type: Boolean,
    default: false,
  },
  recentsNavActive: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'recentsTrigger',
  'selectConversation',
  'renameConversationInitiated',
  'renameConversation',
  'deleteConversationInitiated',
  'deleteConversationCanceled',
  'removeConversation',
  'chatActionsOpened',
])

const { t } = useI18n()

const RECENT_CHAT_BATCH_SIZE = 20
const visibleRecentChatsCount = ref(RECENT_CHAT_BATCH_SIZE)
const openActionsConversationId = ref(null)
const renamingConversationId = ref(null)
const renamingConversation = ref(null)
const titleDraft = ref('')
const renameError = ref('')
const isSavingRename = ref(false)
const deleteCandidate = ref(null)
const showDeleteConfirm = ref(false)
const deleteDialogReference = ref(null)
const editingRowReference = ref(null)
const renameInputReference = ref(null)
let titleSelectClickTimeout = null

function resolveElementReference(referenceValue) {
  if (!referenceValue)
    return null
  return Array.isArray(referenceValue) ? referenceValue.find(Boolean) ?? null : referenceValue
}

function setEditingRowReference(element) {
  editingRowReference.value = element
}

function setRenameInputReference(element) {
  renameInputReference.value = element
}

const sortedConversations = computed(() => {
  return [...props.conversations].sort((a, b) => {
    const aTime = new Date(a?.updated_at || a?.created_at || 0).getTime()
    const bTime = new Date(b?.updated_at || b?.created_at || 0).getTime()
    return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime)
  })
})

const visibleRecentChats = computed(() => sortedConversations.value.slice(0, visibleRecentChatsCount.value))
const hasMoreRecentChats = computed(() => visibleRecentChatsCount.value < sortedConversations.value.length)

watch(() => props.conversations.length, (length) => {
  if (length < visibleRecentChatsCount.value) {
    visibleRecentChatsCount.value = Math.max(RECENT_CHAT_BATCH_SIZE, length)
  }
})

watch(showDeleteConfirm, (value) => {
  nextTick(() => {
    if (value) {
      deleteDialogReference.value?.showModal()
    }
    else {
      deleteDialogReference.value?.close()
    }
  })
})

function getConversationId(conversation) {
  return conversation?.conversation_id || conversation?.id || ''
}

function getConversationTitle(conversation) {
  return conversation?.title || conversation?.name || t('newBradley.searchModal.conversation')
}

function formatDate(value) {
  if (!value)
    return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

function handleRecentChatsScroll(event) {
  const target = event.target
  if (!target || !hasMoreRecentChats.value) {
    return
  }

  const remainingScroll = target.scrollHeight - target.scrollTop - target.clientHeight
  if (remainingScroll <= 48) {
    visibleRecentChatsCount.value += RECENT_CHAT_BATCH_SIZE
  }
}

function handleSelectConversation(conversation, index) {
  emit('selectConversation', { conversation, position: index })
}

function handleTitleClick(conversation, index) {
  if (titleSelectClickTimeout) {
    clearTimeout(titleSelectClickTimeout)
  }

  titleSelectClickTimeout = setTimeout(() => {
    titleSelectClickTimeout = null
    handleSelectConversation(conversation, index)
  }, 200)
}

function handleTitleDblClick(conversation) {
  if (titleSelectClickTimeout) {
    clearTimeout(titleSelectClickTimeout)
    titleSelectClickTimeout = null
  }

  beginRename(conversation)
}

function toggleActionsMenu(conversationId) {
  emit('chatActionsOpened')
  openActionsConversationId.value = openActionsConversationId.value === conversationId ? null : conversationId
}

function closeChatActionsMenu() {
  openActionsConversationId.value = null
}

function beginRename(conversation) {
  const conversationId = getConversationId(conversation)
  if (!conversationId) {
    return
  }

  emit('renameConversationInitiated', { conversationId, source: 'recent_chats' })
  renamingConversationId.value = conversationId
  renamingConversation.value = conversation
  titleDraft.value = getConversationTitle(conversation)
  renameError.value = ''
  closeChatActionsMenu()
}

function cancelRename() {
  renamingConversationId.value = null
  renamingConversation.value = null
  titleDraft.value = ''
  renameError.value = ''
}

async function saveRename(conversation) {
  if (isSavingRename.value) {
    return
  }

  const conversationId = getConversationId(conversation)
  const nextTitle = titleDraft.value.trim()

  if (!nextTitle) {
    renameError.value = 'empty'
    return
  }

  if (nextTitle === getConversationTitle(conversation).trim()) {
    cancelRename()
    return
  }

  isSavingRename.value = true
  renameError.value = ''
  try {
    await new Promise((resolve, reject) => {
      emit('renameConversation', {
        conversationId,
        title: nextTitle,
        source: 'recent_chats',
        onSettled: (error) => {
          if (error) {
            reject(error)
          }
          else {
            resolve()
          }
        },
      })
    })
    cancelRename()
  }
  catch {
    renameError.value = 'request'
  }
  finally {
    isSavingRename.value = false
  }
}

function commitRename() {
  const conversation = renamingConversation.value
    ?? visibleRecentChats.value.find(c => getConversationId(c) === renamingConversationId.value)
  if (conversation) {
    saveRename(conversation)
  }
}

function handleRenamePointerOutside(event) {
  if (!renamingConversationId.value || isSavingRename.value) {
    return
  }

  const editRoot = resolveElementReference(editingRowReference.value) ?? resolveElementReference(renameInputReference.value)
  if (editRoot?.contains(event.target)) {
    return
  }

  commitRename()
}

watch(renamingConversationId, (conversationId) => {
  if (conversationId) {
    nextTick(() => {
      const input = resolveElementReference(renameInputReference.value)
      input?.focus()
      input?.select()
      document.addEventListener('pointerdown', handleRenamePointerOutside, true)
    })
    return
  }

  document.removeEventListener('pointerdown', handleRenamePointerOutside, true)
})

function openDeleteConfirm(conversation) {
  const conversationId = getConversationId(conversation)
  if (!conversationId) {
    return
  }

  deleteCandidate.value = conversation
  showDeleteConfirm.value = true
  closeChatActionsMenu()
  emit('deleteConversationInitiated', { conversationId, source: 'recent_chats' })
}

function cancelDeleteConfirm() {
  const conversationId = getConversationId(deleteCandidate.value)
  if (conversationId) {
    emit('deleteConversationCanceled', { conversationId, source: 'recent_chats' })
  }
  showDeleteConfirm.value = false
  deleteCandidate.value = null
}

function confirmDeleteConversation() {
  const conversationId = getConversationId(deleteCandidate.value)
  if (!conversationId) {
    cancelDeleteConfirm()
    return
  }

  showDeleteConfirm.value = false
  emit('removeConversation', { conversationId, source: 'recent_chats' })
  deleteCandidate.value = null
}

defineExpose({
  closeChatActionsMenu,
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleRenamePointerOutside, true)
  if (titleSelectClickTimeout) {
    clearTimeout(titleSelectClickTimeout)
    titleSelectClickTimeout = null
  }
})
</script>

<template>
  <section
    class="recent-chats-section"
    :class="{ 'recent-chats-section--expanded': sidebarExpanded }"
  >
    <div class="menu-divider" />
    <button
      type="button"
      class="sidebar-menu-item recent-chats-trigger"
      :class="{ 'active': recentsNavActive, 'recent-chats-trigger--open': sectionOpen }"
      :aria-expanded="sidebarExpanded ? sectionOpen : undefined"
      :title="t('newBradley.sidebar.recents')"
      @click="emit('recentsTrigger')"
    >
      <i class="pi pi-history" />
      <span
        v-if="sidebarExpanded"
        class="sidebar-label recent-chats-trigger__label"
      >{{ t('newBradley.sidebar.recents') }}</span>
      <span
        v-if="sidebarExpanded"
        class="recent-chats-trigger__chevron"
        aria-hidden="true"
      >
        <i :class="sectionOpen ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" />
      </span>
    </button>

    <div
      v-if="sidebarExpanded && sectionOpen && (loadingConversations || visibleRecentChats.length)"
      class="recent-chats-list"
      @scroll="handleRecentChatsScroll"
    >
      <div
        v-if="loadingConversations"
        class="recent-chats-empty"
      >
        <i class="pi pi-spinner animate-spin" />
        <span>{{ t('newBradley.searchModal.loadingConversations') }}</span>
      </div>

      <div
        v-else
        class="recent-chats-items"
      >
        <article
          v-for="(conversation, index) in visibleRecentChats"
          :key="getConversationId(conversation)"
          class="recent-chat-row"
          :class="{ active: activeConversationId === getConversationId(conversation), editing: renamingConversationId === getConversationId(conversation) }"
        >
          <template v-if="renamingConversationId === getConversationId(conversation)">
            <div
              :ref="setEditingRowReference"
              class="recent-chat-edit"
            >
              <input
                :ref="setRenameInputReference"
                v-model="titleDraft"
                type="text"
                maxlength="120"
                class="recent-chat-title-input"
                :disabled="isSavingRename"
                @keydown.enter.prevent="saveRename(conversation)"
                @keydown.esc.prevent="cancelRename"
              >
              <div class="recent-chat-edit-actions">
                <button
                  type="button"
                  class="recent-chat-icon-button"
                  :disabled="isSavingRename"
                  :title="t('newBradley.searchModal.save')"
                  @click="saveRename(conversation)"
                >
                  <i
                    v-if="isSavingRename"
                    class="pi pi-spinner animate-spin"
                  />
                  <i
                    v-else
                    class="pi pi-check"
                  />
                </button>
              </div>
              <p
                v-if="renameError === 'empty'"
                class="recent-chat-error"
              >
                {{ t('newBradley.searchModal.renameErrorEmpty') }}
              </p>
              <p
                v-else-if="renameError === 'request'"
                class="recent-chat-error"
              >
                {{ t('newBradley.searchModal.renameErrorRequest') }}
              </p>
            </div>
          </template>

          <template v-else>
            <button
              type="button"
              class="recent-chat-main"
              @click="handleSelectConversation(conversation, index)"
            >
              <span
                class="recent-chat-title"
                @click.stop="handleTitleClick(conversation, index)"
                @dblclick.stop="handleTitleDblClick(conversation)"
              >{{ getConversationTitle(conversation) }}</span>
              <span
                v-if="conversation.updated_at"
                class="recent-chat-date"
              >
                {{ t('newBradley.searchModal.updated') }} {{ formatDate(conversation.updated_at) }}
              </span>
            </button>
            <div class="recent-chat-actions">
              <button
                type="button"
                class="recent-chat-icon-button"
                :aria-label="t('newBradley.sidebar.chatActions')"
                @click.stop="toggleActionsMenu(getConversationId(conversation))"
              >
                <i class="pi pi-ellipsis-v" />
              </button>
              <div
                v-if="openActionsConversationId === getConversationId(conversation)"
                class="recent-chat-menu"
                @click.stop
              >
                <button
                  type="button"
                  @click="beginRename(conversation)"
                >
                  <i class="pi pi-pencil" />
                  <span>{{ t('newBradley.searchModal.rename') }}</span>
                </button>
                <button
                  type="button"
                  class="danger"
                  @click="openDeleteConfirm(conversation)"
                >
                  <i class="pi pi-trash" />
                  <span>{{ t('newBradley.deleteModal.delete') }}</span>
                </button>
              </div>
            </div>
          </template>
        </article>
      </div>
    </div>

    <dialog
      ref="deleteDialogReference"
      class="recent-chat-delete-dialog"
      aria-labelledby="recent-chat-delete-title"
      @close="cancelDeleteConfirm"
    >
      <h3 id="recent-chat-delete-title">
        {{ t('newBradley.deleteModal.title') }}
      </h3>
      <p>{{ t('newBradley.sidebar.deleteChatConfirmMessage') }}</p>
      <div class="recent-chat-delete-actions">
        <button
          type="button"
          class="recent-chat-delete-button"
          @click="confirmDeleteConversation"
        >
          {{ t('newBradley.deleteModal.delete') }}
        </button>
        <button
          type="button"
          class="recent-chat-cancel-button"
          @click="cancelDeleteConfirm"
        >
          {{ t('newBradley.deleteModal.cancel') }}
        </button>
      </div>
    </dialog>
  </section>
</template>

<style scoped>
.menu-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 0.5rem 0;
}

.sidebar-menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: transparent;
  border: none;
  border-right: 3px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #4b5563;
  font-size: 1rem;
  position: relative;
  text-align: left;
}

.sidebar-menu-item:hover {
  background: #e5e7eb;
  color: #1f2937;
}

.sidebar-menu-item.active {
  border-right-color: var(--color-dss-primary-500);
  color: var(--color-dss-primary-500);
  background: #eff6ff;
}

.sidebar-menu-item i {
  flex-shrink: 0;
  width: 20px;
  text-align: center;
  font-size: 1.125rem;
  color: var(--color-dss-primary-500);
}

.sidebar-menu-item:hover i,
.sidebar-menu-item.active i {
  color: var(--color-dss-primary-500);
}

.sidebar-label {
  font-family: var(--font-inter, 'Inter', sans-serif);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.005em;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: #374151;
}

.recent-chats-section--expanded .sidebar-label {
  opacity: 1;
}

.recent-chats-section {
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.recent-chats-trigger {
  flex-shrink: 0;
  width: 100%;
}

.recent-chats-section:not(.recent-chats-section--expanded) .recent-chats-trigger {
  justify-content: center;
  padding: 0.75rem 0.25rem;
}

.recent-chats-trigger__label {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.recent-chats-trigger__chevron {
  display: inline-flex;
  flex-shrink: 0;
  margin-left: auto;
  align-items: center;
  color: #292931;
  opacity: 0.75;
}

.recent-chats-trigger__chevron i {
  font-size: 0.75rem;
}

.recent-chats-list {
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: visible;
  padding: 0 0.5rem;
}

.recent-chats-items {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.recent-chats-empty {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0.5rem;
  color: #6b7280;
  font-size: 0.8125rem;
}

.recent-chats-empty > i {
  color: var(--color-dss-primary-500);
}

.recent-chat-row {
  position: relative;
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 0.25rem;
  border-radius: 0.5rem;
  padding: 0.35rem;
  color: #374151;
}

.recent-chat-row:hover,
.recent-chat-row.active {
  background: #e5e7eb;
}

.recent-chat-row.active .recent-chat-title {
  color: var(--color-dss-primary-500);
}

.recent-chat-row.editing {
  display: block;
  background: #ffffff;
}

.recent-chat-edit {
  width: 100%;
}

.recent-chat-main {
  min-width: 0;
  flex: 1 1 auto;
  border: none;
  background: transparent;
  padding: 0.25rem;
  text-align: left;
  cursor: pointer;
}

.recent-chat-title {
  display: block;
  overflow: hidden;
  color: #374151;
  font-size: 0.8125rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recent-chat-date {
  display: block;
  overflow: hidden;
  margin-top: 0.125rem;
  color: #9ca3af;
  font-size: 0.6875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recent-chat-actions {
  position: relative;
  flex-shrink: 0;
}

.recent-chat-icon-button {
  display: inline-flex;
  width: 1.75rem;
  height: 1.75rem;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: var(--color-dss-primary-500);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.recent-chat-icon-button:hover:not(:disabled) {
  background: #d1d5db;
  color: var(--color-dss-primary-600);
}

.recent-chat-icon-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.recent-chat-menu {
  position: absolute;
  top: 2rem;
  right: 0;
  z-index: 5;
  width: 9rem;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #ffffff;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
}

.recent-chat-menu button {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: transparent;
  padding: 0.625rem 0.75rem;
  color: #374151;
  font-size: 0.8125rem;
  text-align: left;
  cursor: pointer;
}

.recent-chat-menu button:hover {
  background: #f3f4f6;
}

.recent-chat-menu button.danger {
  color: #dc2626;
}

.recent-chat-menu button.danger i {
  color: #dc2626;
}

.recent-chat-menu button:not(.danger) i {
  color: var(--color-dss-primary-500);
}

.recent-chat-title-input {
  width: 100%;
  min-width: 0;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  background: #ffffff;
  padding: 0.5rem;
  color: #374151;
  font-size: 0.8125rem;
}

.recent-chat-title-input:focus {
  border-color: #94a3b8;
  outline: none;
  box-shadow: 0 0 0 2px rgba(37, 39, 79, 0.12);
}

.recent-chat-edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
  margin-top: 0.35rem;
}

.recent-chat-error {
  margin: 0.25rem 0 0;
  color: #dc2626;
  font-size: 0.6875rem;
}

.recent-chat-delete-dialog {
  position: fixed;
  inset: 0;
  width: min(24rem, calc(100vw - 2rem));
  margin: auto;
  border: none;
  border-radius: 1rem;
  padding: 1.5rem;
  background: #ffffff;
  box-shadow: 0 20px 35px rgba(15, 23, 42, 0.22);
}

.recent-chat-delete-dialog::backdrop {
  background: rgba(0, 0, 0, 0.5);
}

.recent-chat-delete-dialog h3 {
  margin: 0;
  color: #111827;
  font-size: 1.125rem;
  font-weight: 700;
}

.recent-chat-delete-dialog p {
  margin: 0.5rem 0 0;
  color: #4b5563;
  font-size: 0.875rem;
}

.recent-chat-delete-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
}

.recent-chat-delete-button,
.recent-chat-cancel-button {
  border: none;
  border-radius: 0.5rem;
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
}

.recent-chat-delete-button {
  background: #dc2626;
  color: #ffffff;
}

.recent-chat-cancel-button {
  background: #232d4b;
  color: #ffffff;
}
</style>
